/**
 * AdminFlyerGeneratorPage Component
 *
 * Admin page for generating branded gym marketing flyers.
 * Calls the backend Cloud Function to generate flyers with real QR codes.
 */

import { useState, type CSSProperties, type ChangeEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/lib/firebase';
import { ImageDropZone } from '@/components/common/ImageDropZone';
import type {
  GenerateFlyerRequest,
  GenerateFlyerResponse,
  FlyerSize,
  FlyerFormat,
} from '@flash/shared';

/**
 * AdminFlyerGeneratorPage - Generate branded gym marketing flyers
 */
export const AdminFlyerGeneratorPage = () => {
  const [gymName, setGymName] = useState('');
  const [selectedSize, setSelectedSize] = useState<FlyerSize>('letter');
  const [selectedFormat, setSelectedFormat] = useState<FlyerFormat>('png');
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [gymLogoFile, setGymLogoFile] = useState<File | null>(null);
  const [gymLogoPreview, setGymLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  // Check if we have either a logo or a gym name (one is required)
  const canGenerate = gymLogoFile || gymName.trim();

  /**
   * Handle gym name change
   */
  const handleGymNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGymName(e.target.value);
    setError(null);
    setPreviewUrl(null);
  };

  /**
   * Handle logo file selection from ImageDropZone
   */
  const handleLogoSelect = (file: File) => {
    setGymLogoFile(file);
    setLogoError(null);
    setError(null);
    setPreviewUrl(null);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setGymLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  /**
   * Remove the selected logo
   */
  const handleLogoRemove = () => {
    setGymLogoFile(null);
    setGymLogoPreview(null);
    setLogoError(null);
    setPreviewUrl(null);
  };

  /**
   * Convert file to base64 string (without data URI prefix)
   */
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URI prefix (e.g., "data:image/png;base64,")
        const parts = result.split(',');
        if (parts.length < 2 || !parts[1]) {
          reject(new Error('Failed to convert file to base64: invalid data URL format'));
          return;
        }
        resolve(parts[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * Generate and download flyer
   */
  const handleGenerate = async () => {
    if (!canGenerate) {
      setError('Please enter a gym name or upload a logo');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const generateFlyer = httpsCallable<GenerateFlyerRequest, GenerateFlyerResponse>(
        getFirebaseFunctions(),
        'generateFlyer'
      );

      // Build request - gym name is optional if logo is provided
      const request: GenerateFlyerRequest = {
        size: selectedSize,
        format: selectedFormat,
      };

      // Add gym name if provided
      if (gymName.trim()) {
        request.gymName = gymName.trim();
      }

      // Add logo if provided
      if (gymLogoFile) {
        request.gymLogoBase64 = await fileToBase64(gymLogoFile);
      }

      const result = await generateFlyer(request);

      const { content, mimeType, filename } = result.data;

      // Convert base64 to blob
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Set preview for images
      if (selectedFormat === 'png') {
        setPreviewUrl(`data:${mimeType};base64,${content}`);
      } else {
        // For SVG, decode and create data URL
        const svgContent = atob(content);
        setPreviewUrl(`data:image/svg+xml,${encodeURIComponent(svgContent)}`);
      }
    } catch (err) {
      console.error('Failed to generate flyer:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate flyer. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <h1 style={titleStyle}>Gym Flyer Generator</h1>
          <p style={subtitleStyle}>Generate branded marketing flyers with QR codes for any gym</p>
        </header>

        {/* Gym Identity Section */}
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>1. Gym Identity</h2>
          <p style={requirementHintStyle}>
            Provide either a gym name or logo (or both). The logo will be displayed if provided.
          </p>

          {/* Gym Name Input */}
          <div style={fieldGroupStyle}>
            <label style={fieldLabelStyle}>Gym Name</label>
            <input
              type="text"
              value={gymName}
              onChange={handleGymNameChange}
              placeholder="e.g., Boulder World, The Cliffs"
              style={inputStyle}
              maxLength={100}
            />
          </div>

          {/* Logo Upload */}
          <div style={fieldGroupStyle}>
            <label style={fieldLabelStyle}>Gym Logo</label>
            <p style={logoHintStyle}>PNG format with transparent background works best.</p>

            <ImageDropZone
              onFileSelect={handleLogoSelect}
              onRemove={handleLogoRemove}
              previewUrl={gymLogoPreview}
              error={logoError}
              onError={setLogoError}
              placeholder="Drag & drop logo or click to browse"
              dragActiveText="Drop logo here"
              previewHeight={100}
            />
          </div>
        </section>

        {/* Options Section */}
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>2. Select Options</h2>

          <div style={optionsGridStyle}>
            {/* Size Selection */}
            <div style={optionGroupStyle}>
              <label style={optionLabelStyle}>Size</label>
              <div style={radioGroupStyle}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="size"
                    value="letter"
                    checked={selectedSize === 'letter'}
                    onChange={() => setSelectedSize('letter')}
                    style={radioInputStyle}
                  />
                  Letter (8.5×11in)
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="size"
                    value="half-letter"
                    checked={selectedSize === 'half-letter'}
                    onChange={() => setSelectedSize('half-letter')}
                    style={radioInputStyle}
                  />
                  Half-Letter (5.5×8.5in)
                </label>
              </div>
            </div>

            {/* Format Selection */}
            <div style={optionGroupStyle}>
              <label style={optionLabelStyle}>Format</label>
              <div style={radioGroupStyle}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="format"
                    value="png"
                    checked={selectedFormat === 'png'}
                    onChange={() => setSelectedFormat('png')}
                    style={radioInputStyle}
                  />
                  PNG (recommended for print)
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="format"
                    value="svg"
                    checked={selectedFormat === 'svg'}
                    onChange={() => setSelectedFormat('svg')}
                    style={radioInputStyle}
                  />
                  SVG (vector, editable)
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* Generate Section */}
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>3. Generate & Download</h2>

          <button
            onClick={handleGenerate}
            disabled={generating || !canGenerate}
            style={{
              ...generateButtonStyle,
              opacity: generating || !canGenerate ? 0.6 : 1,
              cursor: generating || !canGenerate ? 'not-allowed' : 'pointer',
            }}
          >
            {generating ? 'Generating...' : 'Generate & Download Flyer'}
          </button>

          {error && <p style={errorStyle}>{error}</p>}
        </section>

        {/* Preview Section */}
        {previewUrl && (
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Preview</h2>
            <div style={previewContainerStyle}>
              <img src={previewUrl} alt="Generated flyer preview" style={previewImageStyle} />
            </div>
            <p style={previewHintStyle}>
              Your flyer has been downloaded. The preview above shows what was generated.
            </p>
          </section>
        )}

        {/* Info Section */}
        <section style={infoSectionStyle}>
          <h3 style={infoTitleStyle}>About the Flyers</h3>
          <ul style={infoListStyle}>
            <li>Flyers include a scannable QR code that links to flashclimbing.com</li>
            <li>The QR code uses high error correction and works even when printed small</li>
            <li>PNG format is recommended for printing (300 DPI resolution)</li>
            <li>SVG format is vector-based and can be edited in design software</li>
          </ul>
        </section>
      </div>
    </main>
  );
};

// Styles
const containerStyle: CSSProperties = {
  minHeight: '100vh',
  backgroundColor: 'var(--color-bg-primary, #f9fafb)',
  padding: '2rem 1rem',
};

const contentStyle: CSSProperties = {
  maxWidth: '700px',
  margin: '0 auto',
};

const headerStyle: CSSProperties = {
  marginBottom: '2rem',
};

const titleStyle: CSSProperties = {
  fontSize: '1.875rem',
  fontWeight: 700,
  color: 'var(--color-text-primary, #111827)',
  marginBottom: '0.5rem',
};

const subtitleStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const sectionStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary, #ffffff)',
  borderRadius: '0.75rem',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #111827)',
  marginBottom: '0.5rem',
};

const requirementHintStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
  marginBottom: '1.25rem',
};

const fieldGroupStyle: CSSProperties = {
  marginBottom: '1.25rem',
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary, #111827)',
  marginBottom: '0.5rem',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '0.75rem 1rem',
  fontSize: '1rem',
  border: '1px solid var(--color-border, #d1d5db)',
  borderRadius: '0.5rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const logoHintStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary, #6b7280)',
  marginTop: '0.25rem',
  marginBottom: '0.75rem',
};

const optionsGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '1.5rem',
};

const optionGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const optionLabelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
};

const radioGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const radioLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.875rem',
  color: 'var(--color-text-primary, #111827)',
  cursor: 'pointer',
};

const radioInputStyle: CSSProperties = {
  width: '1rem',
  height: '1rem',
  accentColor: '#2D7FF9',
};

const generateButtonStyle: CSSProperties = {
  width: '100%',
  padding: '0.875rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: '#2D7FF9',
  border: 'none',
  borderRadius: '0.5rem',
  transition: 'background-color 0.2s, opacity 0.2s',
};

const errorStyle: CSSProperties = {
  marginTop: '1rem',
  fontSize: '0.875rem',
  color: '#dc2626',
  textAlign: 'center',
};

const previewContainerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
  borderRadius: '0.5rem',
  padding: '1rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const previewImageStyle: CSSProperties = {
  maxWidth: '100%',
  maxHeight: '500px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  borderRadius: '0.25rem',
};

const previewHintStyle: CSSProperties = {
  marginTop: '1rem',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
  textAlign: 'center',
};

const infoSectionStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
  borderRadius: '0.75rem',
  padding: '1.25rem',
};

const infoTitleStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #111827)',
  marginBottom: '0.75rem',
};

const infoListStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary, #6b7280)',
  lineHeight: 1.6,
  paddingLeft: '1.25rem',
  margin: 0,
};

export default AdminFlyerGeneratorPage;
